// ── V156: 회의 탭 — 회의 기록 + 테스크 (내용 · 담당자 · 마감일) ─────────────────────────
// 사장님: "회의를 누르면 회의 기록과, 어떤 테스크가 주어지면 그걸 누르면 그 테스크 내용과
//          담당자 선택, 마감일(시간은 필요없고 날짜만)을 선택하게. 회의가 저장되면 오늘의 작업에 그 테스크가 포함되게"
//   · 목록 → 회의 하나를 누르면 전체 화면 편집 (PC는 왼쪽 회의 기록 · 오른쪽 테스크)
//   · 테스크를 누르면 그 자리에서 내용 · 담당자 · 마감일을 고친다
//   · 💾 회의 저장 → 테스크가 오늘의 작업에 들어간다 (WorkToday.saveMeeting → utils/meetingLink.applyMeetingToWork)
//   · 테스크 옆에 지금 상태: 저장하면 들어가요 · 진행 중 · ✓ 완료(누가·언제) · 오늘의 작업에 없음
// ── V157 ── "회의도 저장을 누르지 않고 작성하는 대로 저장되도록"
//   · 적는 대로 0.8초 뒤 자동 저장 (내용이 있을 때만). 회의 목록으로 나가거나 탭을 바꾸면 바로 저장
//   · 테스크도 적는 대로 — '＋ 테스크 추가'를 누르면 빈 테스크가 생기고, 글을 적으면 오늘의 작업에 바로 들어간다
//   · 저장 버튼 대신 '✓ 저장됨' 표시
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fmtDay } from '../utils/workCalc';
import { fmtDue, dueState, TASK_MAX } from '../utils/workMeta';
import { newMeetingId, newTaskId, taskStatus, buildMeetingText, splitForWechat, TITLE_MAX, NOTES_MAX } from '../utils/meetingLink';
import { OwnerPicker, DuePicker, FieldRow, chipStyle, textareaStyle, textInputStyle, shareText, ShareState, ORANGE, GREEN } from './WorkFields';

const hhmm = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const blank = (today) => ({ id: newMeetingId(), date: today, title: '', notes: '', tasks: [], createdAt: Date.now() });
// 저장할 내용의 지문 — 빈 테스크·앞뒤 빈칸은 빼고 본다 (바뀐 게 없으면 저장하지 않는다)
const sig = (m) => JSON.stringify({
  d: m.date, t: String(m.title || '').trim(), n: String(m.notes || '').replace(/\s+$/, ''),
  k: (m.tasks || []).filter(t => String(t.text || '').trim()).map(t => [t.id, String(t.text).trim(), String(t.owner || '').trim(), t.due || '']),
});
const hasContent = (m) => !!(m && (String(m.title || '').trim() || String(m.notes || '').trim() || (m.tasks || []).some(t => String(t.text || '').trim())));
const AUTOSAVE_MS = 800;

export default function MeetingPane({
  Z, lang, isMobile, card, btn, today, storeName = '',
  meetings = [], workToday = { items: [] }, workChecks = {}, workHistory = [], owners = [],
  openId = null, onOpened, onSave, onDelete,
}) {
  const [draft, setDraft] = useState(null);     // 편집 중인 회의 (null = 목록)
  const [taskOpen, setTaskOpen] = useState(null); // 펼친 테스크 id
  const [confirmDel, setConfirmDel] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [saveState, setSaveState] = useState(''); // '' | 'pending' | 'saved'

  const stored = useMemo(() => new Map((meetings || []).map(m => [m.id, m])), [meetings]);
  const items = Array.isArray(workToday?.items) ? workToday.items : [];

  // ── V157 자동 저장 ────────────────────────────────────────────────────────
  const draftRef = useRef(null);
  draftRef.current = draft;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const timerRef = useRef(null);
  const lastSaved = useRef('');
  const flush = () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    const d = draftRef.current;
    if (!d || !hasContent(d)) return;
    const s2 = sig(d);
    if (s2 === lastSaved.current) return;
    lastSaved.current = s2;
    onSaveRef.current && onSaveRef.current(d);
    setSaveState('saved');
  };
  useEffect(() => {
    if (!draft || !hasContent(draft) || sig(draft) === lastSaved.current) return undefined;
    setSaveState('pending');
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, AUTOSAVE_MS);
    return undefined;
  }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => flush(), []); // eslint-disable-line react-hooks/exhaustive-deps   (탭을 바꾸면 바로 저장)

  const open = (m) => {
    flush();
    const d = JSON.parse(JSON.stringify(m));
    lastSaved.current = sig(d);
    setDraft(d); setTaskOpen(null); setConfirmDel(false); setSaveState(stored.has(m.id) ? 'saved' : '');
  };
  const openNew = () => {
    flush();
    const d = blank(today);
    lastSaved.current = sig(d);
    setDraft(d); setTaskOpen(null); setConfirmDel(false); setSaveState('');
  };
  useEffect(() => {
    if (!openId) return;
    const m = stored.get(openId);
    if (m) open(m);
    if (onOpened) onOpened();
  }, [openId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 빈 테스크(글 없음)는 닫을 때 지운다
  const dropEmpty = (d, keepId = null) => ({ ...d, tasks: (d.tasks || []).filter(t => t.id === keepId || String(t.text || '').trim()) });
  const back = () => {
    flush();
    setDraft(null); setTaskOpen(null); setShareOpen(false); setSaveState('');
  };

  const statusOf = (t) => {
    const saved = !!(stored.get(draft?.id)?.tasks || []).some(x => x.id === t.id);
    return taskStatus(t, { items, workChecks, history: workHistory, saved });
  };

  // ── 테스크 — 적는 대로 바로 바뀐다 ─────────────────────────────────────────
  const patchTask = (id, patch) => setDraft(d => ({ ...d, tasks: (d.tasks || []).map(t => (t.id === id ? { ...t, ...patch } : t)) }));
  const addTask = () => {
    const t = { id: newTaskId(), text: '', owner: '', due: '' };
    setDraft(d => ({ ...dropEmpty(d), tasks: [...dropEmpty(d).tasks, t] }));
    setTaskOpen(t.id);
  };
  const closeTask = () => { setDraft(d => dropEmpty(d)); setTaskOpen(null); };
  const toggleTask = (id) => {
    if (taskOpen === id) { closeTask(); return; }
    setDraft(d => dropEmpty(d, id));
    setTaskOpen(id);
  };
  const removeTask = (id) => { setDraft(d => ({ ...d, tasks: (d.tasks || []).filter(t => t.id !== id) })); setTaskOpen(null); };
  const doDelete = () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    const id = draft.id;
    draftRef.current = null;
    setConfirmDel(false); setDraft(null); setTaskOpen(null); setSaveState('');
    if (stored.has(id)) onDelete(id);
  };

  // ── 목록 ────────────────────────────────────────────────────────────────
  if (!draft) {
    return (
      <div data-meeting-list>
        <div style={{ ...card, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#18181B' }}>🗓 {Z('회의 기록', '会议记录')}</div>
            <div style={{ fontSize: 13, color: '#71717A', marginTop: 2 }}>
              {Z('회의 내용을 적고 테스크를 나눠 주세요. 저장하면 테스크가 오늘의 작업에 들어가요.', '记下会议内容、分配任务。保存后任务会加到今日工作里。')}
            </div>
          </div>
          <button type="button" data-meeting-new onClick={openNew} style={{ ...btn(ORANGE, '#fff'), padding: '10px 16px', fontSize: 14 }}>＋ {Z('새 회의', '新会议')}</button>
        </div>
        {(meetings || []).length === 0 && (
          <div data-meeting-empty style={{ ...card, textAlign: 'center', padding: '40px 16px', color: '#71717A' }}>
            <div style={{ fontSize: 34 }}>🗓</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#18181B', marginTop: 6 }}>{Z('아직 회의 기록이 없어요', '还没有会议记录')}</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>{Z('＋ 새 회의를 눌러 시작하세요.', '点「＋ 新会议」开始。')}</div>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))', gap: 10 }}>
          {(meetings || []).map(m => {
            const st = (m.tasks || []).map(t => taskStatus(t, { items, workChecks, history: workHistory }));
            const done = st.filter(s => s.state === 'done').length;
            const over = (m.tasks || []).filter((t, k) => st[k].state !== 'done' && dueState(t, { today }) === 'over').length;
            const who = [...new Set((m.tasks || []).map(t => t.owner).filter(Boolean))];
            return (
              <button key={m.id} type="button" data-meeting-open={m.id} onClick={() => open(m)}
                style={{ ...card, textAlign: 'left', border: '1.5px solid #fff', cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'flex-start', width: '100%' }}>
                <div style={{ minWidth: 62, textAlign: 'center', background: '#FFF4EC', borderRadius: 10, padding: '6px 4px' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#C2410C' }}>{m.date ? fmtDay(m.date, lang === 'zh' ? 'zh' : 'ko').replace(/\s*\(.*\)|\s*星期.*/, '') : ''}</div>
                  <div style={{ fontSize: 12, color: '#9A3412' }}>{m.date ? (lang === 'zh' ? fmtDay(m.date, 'zh').replace(/^.*?(星期.)$/, '$1') : (fmtDay(m.date, 'ko').match(/\((.)\)/) || [])[1] || '') : ''}</div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 900, color: '#18181B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.title || Z('회의', '会议')}</div>
                  {m.notes && (
                    <div style={{ fontSize: 13, color: '#52525B', marginTop: 3, lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}>{m.notes}</div>
                  )}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6, fontSize: 12, fontWeight: 700 }}>
                    <span data-meeting-progress style={{ color: (m.tasks || []).length && done === m.tasks.length ? '#16A34A' : '#3F3F46', background: '#F4F4F5', borderRadius: 6, padding: '1px 7px' }}>
                      📌 {Z('테스크', '任务')} {(m.tasks || []).length ? `${done}/${m.tasks.length}` : '0'}
                    </span>
                    {over > 0 && <span style={{ color: '#B91C1C', background: '#FEE2E2', borderRadius: 6, padding: '1px 7px' }}>⏰ {Z(`마감 지남 ${over}`, `已过期 ${over}`)}</span>}
                    {who.slice(0, 4).map(w => <span key={w} style={{ color: '#1D4ED8', background: '#EFF6FF', borderRadius: 6, padding: '1px 7px' }}>👤 {w}</span>)}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ── 회의 편집 (전체 화면) ──────────────────────────────────────────────────
  const isStored = stored.has(draft.id);
  const tasks = draft.tasks || [];
  const stateChip = (s, t) => {
    let st; let txt; let color; let bg;
    if (s.state === 'new') { st = 'new'; txt = Z('저장하면 오늘의 작업에 들어가요', '保存后加到今日工作'); color = '#C2410C'; bg = '#FFF4EC'; }
    else if (s.state === 'done') { st = 'done'; txt = `✓ ${Z('완료', '已完成')}${s.at ? ` ${hhmm(s.at)}` : ''}${s.by ? ` · ${s.by}` : ''}`; color = '#15803D'; bg = '#DCFCE7'; }
    else if (s.state === 'missing') { st = 'missing'; txt = Z('오늘의 작업에 없음 · 저장하면 다시 들어가요', '不在今日工作里 · 保存后会重新加入'); color = '#71717A'; bg = '#F4F4F5'; }
    else {
      const over = dueState(t, { today }) === 'over';
      st = 'open'; txt = over ? Z('마감 지남 · 오늘의 작업에 있어요', '已过期 · 在今日工作里') : Z('진행 중 · 오늘의 작업에 있어요', '进行中 · 在今日工作里');
      color = over ? '#B91C1C' : '#1D4ED8'; bg = over ? '#FEE2E2' : '#EFF6FF';
    }
    return <span data-task-state={st} style={{ color, background: bg, borderRadius: 6, padding: '1px 7px' }}>{txt}</span>;
  };

  const taskForm = (t) => (
    <div data-task-form={t.id} style={{ border: '1.5px solid #FDBA74', background: '#FFFBF7', borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 8 }}>
      <FieldRow label={`📌 ${Z('테스크', '任务')}`} top>
        <textarea data-task-text autoFocus rows={2} maxLength={TASK_MAX} value={t.text || ''}
          onChange={e => patchTask(t.id, { text: e.target.value })}
          placeholder={Z('예: 닭강정 소스 2가지 만들어서 맛보기', '例：做两种炸鸡酱料试味道')} style={{ ...textareaStyle, fontSize: 15, fontWeight: 700 }} />
      </FieldRow>
      <FieldRow label={`👤 ${Z('담당자', '负责人')}`} top>
        <OwnerPicker Z={Z} owners={owners} value={t.owner || ''} onChange={v => patchTask(t.id, { owner: v })} attr="task" />
      </FieldRow>
      <FieldRow label={`⏰ ${Z('마감일', '截止')}`} top>
        <DuePicker Z={Z} today={today} value={t.due || ''} onChange={v => patchTask(t.id, { due: v })} />
      </FieldRow>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" data-task-remove onClick={() => removeTask(t.id)} style={{ ...btn('#fff', '#DC2626', '#FECACA'), marginRight: 'auto' }}>🗑 {Z('테스크 빼기', '删除任务')}</button>
        <span style={{ fontSize: 12, color: '#71717A' }}>{String(t.text || '').trim() ? Z('적는 대로 오늘의 작업에 들어가요', '写好就会加到今日工作') : Z('내용을 적어 주세요', '请写任务内容')}</span>
        <button type="button" data-task-done onClick={closeTask} style={btn(ORANGE, '#fff')}>{Z('✓ 다 적었어요', '✓ 写好了')}</button>
      </div>
    </div>
  );

  return (
    <div data-meeting-editor={draft.id}>
      <div style={{ ...card, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" data-meeting-back onClick={back} style={btn('#fff', '#18181B', '#D4D4D8')}>← {Z('회의 목록', '会议列表')}</button>
        <div style={{ flex: 1, fontSize: 18, fontWeight: 900, minWidth: 120 }}>
          🗓 {isStored ? Z('회의 기록', '会议记录') : Z('새 회의', '新会议')}
          <span data-meeting-save-state={saveState || (hasContent(draft) ? 'pending' : 'empty')}
            style={{ marginLeft: 10, fontSize: 12, fontWeight: 700, color: saveState === 'saved' ? '#16A34A' : '#A1A1AA' }}>
            {saveState === 'saved' ? Z('✓ 자동 저장됨', '✓ 已自动保存') : saveState === 'pending' ? Z('저장 중…', '保存中…') : Z('적으면 자동으로 저장돼요', '写了就会自动保存')}
          </span>
        </div>
        <button type="button" data-meeting-share onClick={() => setShareOpen(v => !v)} style={btn(shareOpen ? GREEN : '#fff', shareOpen ? '#fff' : '#15803D', GREEN)}>💬 {Z('위챗', '微信')}</button>
        {isStored && (confirmDel ? (
          <>
            <button type="button" data-meeting-delete-confirm onClick={doDelete} style={btn('#DC2626', '#fff')}>{Z('정말 지우기', '确定删除')}</button>
            <button type="button" onClick={() => setConfirmDel(false)} style={btn('#fff', '#52525B', '#D4D4D8')}>{Z('취소', '取消')}</button>
          </>
        ) : (
          <button type="button" data-meeting-delete onClick={() => setConfirmDel(true)} style={btn('#fff', '#DC2626', '#FECACA')}>🗑 {Z('삭제', '删除')}</button>
        ))}
      </div>
      {confirmDel && (
        <div style={{ ...card, marginBottom: 10, background: '#FEF2F2', fontSize: 13, color: '#991B1B', fontWeight: 700 }}>
          {Z('이 회의를 지우면, 오늘의 작업에 들어간 이 회의 테스크 중 아직 안 한 것도 같이 빠져요.', '删除这个会议后，今日工作里这个会议还没做的任务也会一起删除。')}
        </div>
      )}
      {shareOpen && <MeetingShare Z={Z} isMobile={isMobile} meeting={draft} today={today} storeName={storeName} statusOf={statusOf} card={card} />}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.25fr) minmax(0, 1fr)', gap: 12, alignItems: 'start' }}>
        {/* 회의 기록 */}
        <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input data-meeting-date type="date" value={draft.date || ''} onChange={e => setDraft(d => ({ ...d, date: e.target.value || today }))}
              style={{ ...textInputStyle, width: 160, flex: '0 0 auto' }} />
            <input data-meeting-title autoFocus={!isStored && !draft.title} value={draft.title} maxLength={TITLE_MAX} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
              placeholder={Z('회의 제목 — 예: 주간 회의, 신메뉴 회의', '会议标题 —— 例：周会、新菜单会议')}
              style={{ ...textInputStyle, flex: 1, minWidth: 180, fontSize: 16, fontWeight: 800 }} />
          </div>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#3F3F46' }}>📝 {Z('회의 기록', '会议内容')}</div>
          <textarea data-meeting-notes value={draft.notes} maxLength={NOTES_MAX} onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
            rows={isMobile ? 9 : 16}
            placeholder={Z('무슨 이야기를 했는지 적어 주세요.\n예) 다음 주 신메뉴 — 닭강정 소스 2가지 맛보기\n    청소 당번 — 월·수·금 小王', '记下讨论的内容。\n例）下周新菜单 —— 试两种炸鸡酱料\n    打扫值日 —— 周一、三、五 小王')}
            style={{ ...textareaStyle, fontSize: 15, minHeight: isMobile ? 180 : 320 }} />
          <div style={{ fontSize: 11.5, color: '#A1A1AA', textAlign: 'right' }}>{draft.notes.length} / {NOTES_MAX}</div>
        </div>

        {/* 테스크 */}
        <div style={{ ...card }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1, fontSize: 16, fontWeight: 900 }}>📌 {Z('테스크', '任务')} <span style={{ color: '#71717A', fontWeight: 700 }}>{tasks.length}</span></div>
            <button type="button" data-task-add onClick={addTask} style={btn(ORANGE, '#fff')}>＋ {Z('테스크 추가', '添加任务')}</button>
          </div>
          {tasks.length === 0 && (
            <div style={{ fontSize: 13, color: '#71717A', padding: '14px 4px', lineHeight: 1.6 }}>
              {Z('회의에서 맡긴 일을 ＋ 테스크 추가로 넣어 주세요. 담당자와 마감일을 정할 수 있어요.', '把会上分配的工作用「＋ 添加任务」加进来，可以选负责人和截止日期。')}
            </div>
          )}
          {tasks.map((t, k) => {
            if (taskOpen === t.id) return <div key={t.id}>{taskForm(t)}</div>;
            const s = statusOf(t);
            const due = fmtDue(t, lang, today);
            const ds = s.state === 'done' ? '' : dueState(t, { today });
            return (
              <button key={t.id} type="button" data-task-open={t.id}
                onClick={() => toggleTask(t.id)}
                style={{ display: 'flex', gap: 10, width: '100%', textAlign: 'left', padding: '10px 10px', marginBottom: 6, borderRadius: 11, cursor: 'pointer',
                         border: `1.5px solid ${s.state === 'done' ? '#BBF7D0' : '#F4F4F5'}`, background: s.state === 'done' ? '#F7FEF9' : '#FAFAFA' }}>
                <span style={{ width: 22, height: 22, borderRadius: 999, background: s.state === 'done' ? '#16A34A' : '#E4E4E7', color: s.state === 'done' ? '#fff' : '#52525B',
                               fontSize: 12, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{s.state === 'done' ? '✓' : k + 1}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 15, fontWeight: 800, color: s.state === 'done' ? '#71717A' : '#18181B', textDecoration: s.state === 'done' ? 'line-through' : 'none', wordBreak: 'break-word' }}>{t.text}</span>
                  <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4, fontSize: 12, fontWeight: 800 }}>
                    <span data-task-owner style={{ color: t.owner ? '#1D4ED8' : '#A1A1AA', background: t.owner ? '#EFF6FF' : '#F4F4F5', borderRadius: 6, padding: '1px 7px' }}>👤 {t.owner || Z('담당 없음', '未指定')}</span>
                    <span data-task-due style={{ color: ds === 'over' ? '#B91C1C' : ds === 'today' ? '#C2410C' : (due ? '#3F3F46' : '#A1A1AA'),
                                                  background: ds === 'over' ? '#FEE2E2' : ds === 'today' ? '#FFF4EC' : '#F4F4F5', borderRadius: 6, padding: '1px 7px' }}>
                      ⏰ {due ? `${due}${Z('까지', '前')}` : Z('마감 없음', '没截止')}
                    </span>
                  </span>
                  <span style={{ display: 'inline-block', marginTop: 5, fontSize: 11.5, fontWeight: 700 }}>
                    {stateChip(s, t)}
                  </span>
                </span>
                <span style={{ color: '#A1A1AA', fontSize: 15, alignSelf: 'center' }}>✎</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* V157 자동 저장 — 저장 버튼 없음 */}
      <div style={{ ...card, marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ flex: 1, fontSize: 13, color: '#52525B', minWidth: 160 }}>
          {Z('적는 대로 자동으로 저장돼요. 테스크는 바로 오늘의 작업에 들어가고, 고치면 같이 바뀌어요.', '写的内容会自动保存。任务会马上加到今日工作，修改也会同步。')}
        </span>
        <button type="button" data-meeting-finish onClick={back} style={{ ...btn(ORANGE, '#fff'), padding: '10px 18px', fontSize: 15 }}>✓ {Z('다 했어요', '完成')}</button>
      </div>
    </div>
  );
}

// ── 회의록 위챗으로 보내기 ────────────────────────────────────────────────────
const LS_MSHARE = 'semfit_meeting_share_v1';
function MeetingShare({ Z, isMobile, meeting, today, storeName, statusOf, card }) {
  const [mode, setMode] = useState(() => { try { const v = localStorage.getItem(LS_MSHARE); return ['bi', 'zh', 'ko'].includes(v) ? v : 'bi'; } catch (e) { return 'bi'; } });
  const [sent, setSent] = useState({});
  const text = buildMeetingText({ ...meeting, tasks: (meeting.tasks || []).filter(t => String(t.text || '').trim()) }, { mode, today, storeName, statusOf });
  const parts = splitForWechat(text);
  const key = parts.join('\u0001');
  useEffect(() => { setSent({}); }, [key]);
  const pick = (m) => { setMode(m); try { localStorage.setItem(LS_MSHARE, m); } catch (e) { /* 무시 */ } };
  return (
    <div data-meeting-share-box style={{ ...card, marginBottom: 10, border: '1.5px solid #BBF7D0' }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 800, marginRight: 4 }}>💬 {Z('회의록 보내기', '发会议记录')}</span>
        {[['bi', '한국어 + 中文'], ['zh', '中文'], ['ko', '한국어']].map(([k, l]) => (
          <button key={k} type="button" data-meeting-share-mode={k} onClick={() => pick(k)} style={chipStyle(mode === k, 'green')}>{l}</button>
        ))}
        <span style={{ fontSize: 12, color: '#71717A' }}>{isMobile ? Z('공유창에서 위챗을 고르세요', '在分享里选微信') : Z('복사한 뒤 위챗에 붙여넣기', '复制后粘贴到微信')}</span>
      </div>
      {parts.map((p, k) => (
        <div key={k} style={{ marginBottom: 8 }}>
          <pre data-meeting-share-text={k} style={{ margin: 0, padding: 12, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, fontSize: 12.5,
                                                  whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 240, overflowY: 'auto', fontFamily: 'inherit', userSelect: 'text', WebkitUserSelect: 'text', lineHeight: 1.55 }}>{p}</pre>
          <button type="button" data-meeting-share-send={k} onClick={async () => { const r = await shareText(p, isMobile); if (r) setSent(s => ({ ...s, [k]: r })); }}
            style={{ marginTop: 6, width: '100%', padding: 11, borderRadius: 10, border: 'none', background: GREEN, color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
            💬 {Z('위챗으로 보내기', '发微信')}{parts.length > 1 ? ` (${k + 1}/${parts.length})` : ''}
          </button>
          <ShareState Z={Z} state={sent[k]} />
        </div>
      ))}
    </div>
  );
}
